/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useLocation } from "@epic-mw/app-context";
import { useLocale } from "@epic-mw/i18n";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import useSWRImmutable from "swr";

import { useAccountContext } from "../account/account.hooks";

import { AuthRes, auth } from "./auth.fetch";

export const useAccountLoginRequired = (): { accountLoading: boolean; isLoggedIn?: boolean } => {
	const { account, isLoading } = useAccountContext();
	const router = useRouter();
	const location = useLocation();
	const locale = useLocale();
	const isLoggedIn = account.isLoggedIn;
	if (account && !isLoading && !isLoggedIn) {
		console.info("useAccountLoginRequired() redirecting to /auth");
		router.push(`/${locale}/auth?redirectUrl=${location.toString()}`);
	}
	return { accountLoading: isLoading, isLoggedIn };
};

export const useAuth = (
	disable?: boolean,
): {
	auth?: AuthRes;
	isLoading: boolean;
	error?: string;
} => {
	const { refreshAccount } = useAccountContext();
	const { data, error } = useSWRImmutable(!disable ? "auth" : null, auth, {
		errorRetryCount: 1,
	});

	// Trigger account refresh when tokens are upgraded
	useEffect(() => {
		console.info("Token upgraded, refreshing account data");
		void refreshAccount();
	}, [data, refreshAccount]);

	return {
		auth: data,
		isLoading: !data && !error,
		error,
	};
};
